import type { PodcastEpisodeView, PodcastFeedKind } from '@almanac/contracts';
import { toLocalYmd } from './format';

/** What to tell the Cowork assistant to opt into the weekly brief. */
export const WEEKLY_BRIEF_PROMPT = 'Set up my weekly audio brief';

/** The steps for following a private feed by URL, per podcast app. */
export const PODCAST_APP_STEPS = [
  { app: 'Apple Podcasts', steps: 'Library → ⋯ → Follow a Show by URL, then paste the link.' },
  { app: 'Overcast', steps: '+ → Add URL, then paste the link.' },
  { app: 'Pocket Casts', steps: 'Paste the link into search.' },
] as const;

export const FEED_LABELS: Record<PodcastFeedKind, string> = {
  company: 'Company',
  personal: 'Your brief',
};

/** `180` → `3 min`; under a minute shows seconds. */
export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds} sec`;
  return `${Math.round(seconds / 60)} min`;
}

/**
 * The `YYYY-MM-DD` day an episode belongs to: its publish instant in the
 * viewer's timezone, the same zone digest dates are keyed in. An episode
 * published at 23:30 Denver time belongs to that evening, not the next UTC day.
 */
export function episodeDay(episode: Pick<PodcastEpisodeView, 'published_at'>): string | null {
  const published = new Date(episode.published_at);
  if (Number.isNaN(published.getTime())) return null;
  return toLocalYmd(published);
}

/** The newest episode of each feed; `episodes` arrive newest first. */
export function latestByFeed(episodes: PodcastEpisodeView[]): PodcastEpisodeView[] {
  const latest = new Map<PodcastFeedKind, PodcastEpisodeView>();
  for (const episode of episodes) {
    if (!latest.has(episode.feed)) latest.set(episode.feed, episode);
  }
  return [...latest.values()];
}

const LINEAR_ISSUE = /^[A-Z]{2,10}-\d+$/u;
const GITHUB_PULL = /^https:\/\/github\.com\/[^/]+\/([^/]+)\/pull\/(\d+)/u;
const GITHUB_BLOB = /^https:\/\/github\.com\/[^/]+\/[^/]+\/blob\/[^/]+\/(?:.+\/)?([^/]+)$/u;

/**
 * A link for an action item's ref: a Linear issue id (`ENG-1234`) or a URL.
 * Labels are short so several fit on one line: `ENG-1234`, `langston-api#1284`,
 * a file name, or the host. Anything else is shown as plain text.
 */
export function refLink(ref: string): { href: string | null; label: string } {
  if (LINEAR_ISSUE.test(ref)) {
    return { href: `https://linear.app/the-langston-co/issue/${ref}`, label: ref };
  }
  let url: URL;
  try {
    url = new URL(ref);
  } catch {
    return { href: null, label: ref };
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return { href: null, label: ref };
  const pull = GITHUB_PULL.exec(ref);
  if (pull) return { href: ref, label: `${pull[1]}#${pull[2]}` };
  const blob = GITHUB_BLOB.exec(ref);
  if (blob) {
    let label = blob[1]!;
    try {
      label = decodeURIComponent(label);
    } catch {
      // Malformed percent escapes: show the file name as written.
    }
    return { href: ref, label };
  }
  if (url.hostname === 'linear.app') {
    if (url.pathname.includes('/document/')) return { href: ref, label: 'Linear doc' };
    if (url.pathname.includes('/project')) return { href: ref, label: 'Linear project' };
    return { href: ref, label: 'Linear' };
  }
  return { href: ref, label: url.hostname };
}

let playing: HTMLMediaElement | null = null;

/**
 * One clip plays at a time across the app, episode or narrator preview:
 * starting a player pauses whichever was playing.
 */
export function claimPlayback(player: HTMLMediaElement): void {
  if (playing && playing !== player) playing.pause();
  playing = player;
}

/** Stops a player leaving the page, so no audio keeps going without controls. */
export function releasePlayback(player: HTMLMediaElement): void {
  player.pause();
  if (playing === player) playing = null;
}
